import { useEffect, useState } from 'react';
import {
  pick,
  type KitchenCatalog,
  type KitchenConfig,
  type KitchenMaterial,
  type KitchenProjectInfo,
  type Lighting,
  type ZoneKey,
} from '@cfg/shared';
import { Alert, Badge, Button, Dialog, TextArea, TextField, useToast } from '@cfg/ui';
import { downloadBlob, exportPdf, saveConfiguration } from '../api';
import { useI18n, type DictKey } from '../i18n';
import { stockLabelKey, stockTone } from './MaterialBrowser';

const PROJECT_KEY = 'cfg-kitchen-project';

function loadProject(): KitchenProjectInfo {
  try {
    return JSON.parse(localStorage.getItem(PROJECT_KEY) ?? '{}') as KitchenProjectInfo;
  } catch {
    return {};
  }
}

/** Reads an image and scales it down to at most 600 × 240 px (PNG) for the PDF header. */
async function readLogo(file: File): Promise<string> {
  if (file.size > 5 * 1024 * 1024 || !/^image\/(png|jpeg|webp)$/.test(file.type)) {
    throw new Error('unsupported logo');
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 600 / bitmap.width, 240 / bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const dataUrl = canvas.toDataURL('image/png');
  if (dataUrl.length > 400_000) throw new Error('logo too large');
  return dataUrl;
}

export function ExportDialog({
  open,
  onClose,
  catalog,
  config,
}: {
  open: boolean;
  onClose: () => void;
  catalog: KitchenCatalog;
  config: KitchenConfig;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const [project, setProject] = useState<KitchenProjectInfo>(loadProject);
  const [scenes, setScenes] = useState<string[]>([config.sceneKey]);
  const [lightings, setLightings] = useState<Lighting[]>(['day', 'night']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setScenes([config.sceneKey]);
      setError(null);
    }
  }, [open, config.sceneKey]);

  const set = (key: keyof KitchenProjectInfo) => (e: { target: { value: string } }) =>
    setProject((p) => ({ ...p, [key]: e.target.value }));

  const toggle = <T,>(list: T[], value: T) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  const images = scenes.length * lightings.length;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(project)
          .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
          .filter(([, v]) => v),
      ) as KitchenProjectInfo;
      try {
        localStorage.setItem(PROJECT_KEY, JSON.stringify(cleaned));
      } catch {
        // storage unavailable
      }
      const { blob, filename } = await exportPdf({
        config,
        sceneKeys: scenes,
        lightings,
        project: cleaned,
        locale,
      });
      downloadBlob(blob, filename);
      toast(t('exportDone'));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('exportTitle')}
      closeLabel={t('close')}
      width={620}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={images === 0 || images > 8}
            onClick={() => void submit()}
          >
            {busy ? t('generating') : t('generate')}
          </Button>
        </>
      }
    >
      <p className="muted" style={{ marginBottom: 16 }}>
        {t('exportHint')}
      </p>
      <div className="form-grid">
        <TextField
          label={t('projectName')}
          value={project.projectName ?? ''}
          onChange={set('projectName')}
          maxLength={120}
        />
        <TextField
          label={t('clientName')}
          value={project.clientName ?? ''}
          onChange={set('clientName')}
          maxLength={120}
        />
        <TextField
          label={t('designerName')}
          value={project.designerName ?? ''}
          onChange={set('designerName')}
          maxLength={120}
        />
        <TextField
          label={t('company')}
          value={project.company ?? ''}
          onChange={set('company')}
          maxLength={120}
        />
        <TextField
          label={t('phone')}
          type="tel"
          value={project.phone ?? ''}
          onChange={set('phone')}
          maxLength={40}
        />
        <TextField
          label={t('email')}
          type="email"
          value={project.email ?? ''}
          onChange={set('email')}
          maxLength={200}
        />
      </div>
      <TextArea
        label={t('notes')}
        value={project.notes ?? ''}
        onChange={set('notes')}
        maxLength={2000}
        style={{ marginTop: 12 }}
      />
      <div className="logo-field">
        <span className="logo-field__label" id="logo-label">
          {t('designerLogo')}
        </span>
        {project.logo ? (
          <div className="logo-field__preview">
            <img src={project.logo} alt={t('designerLogo')} />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setProject((p) => ({ ...p, logo: undefined }))}
            >
              {t('removeLogo')}
            </Button>
          </div>
        ) : (
          <label className="cfg-btn cfg-btn--sm cfg-btn--secondary logo-field__pick">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              aria-labelledby="logo-label"
              className="cfg-visually-hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                void readLogo(file)
                  .then((logo) => setProject((p) => ({ ...p, logo })))
                  .catch(() => setError(t('logoInvalid')));
              }}
            />
            {t('chooseLogo')}
          </label>
        )}
        <span className="muted logo-field__hint">{t('logoHint')}</span>
      </div>
      <fieldset className="check-group">
        <legend>{t('includeViews')}</legend>
        {catalog.scenes.map((s) => (
          <label key={s.key} className="cfg-checkbox">
            <input
              type="checkbox"
              checked={scenes.includes(s.key)}
              onChange={() => setScenes((l) => toggle(l, s.key))}
            />
            <span>{pick(s.name, locale)}</span>
          </label>
        ))}
      </fieldset>
      <fieldset className="check-group">
        <legend>{t('includeLighting')}</legend>
        {(['day', 'night'] as const).map((l) => (
          <label key={l} className="cfg-checkbox">
            <input
              type="checkbox"
              checked={lightings.includes(l)}
              onChange={() => setLightings((x) => toggle(x, l))}
            />
            <span>{t(l)}</span>
          </label>
        ))}
      </fieldset>
      {error && (
        <div style={{ marginTop: 12 }}>
          <Alert tone="error">{error}</Alert>
        </div>
      )}
    </Dialog>
  );
}

export function ShareDialog({
  open,
  onClose,
  config,
}: {
  open: boolean;
  onClose: () => void;
  config: KitchenConfig;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setUrl(null);
    setError(null);
    saveConfiguration(config)
      .then((r) => {
        if (!cancelled) setUrl(`${location.origin}${location.pathname}?c=${r.code}`);
      })
      .catch(() => !cancelled && setError(t('errorGeneric')));
    return () => {
      cancelled = true;
    };
  }, [open, config, t]);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast(t('copied'));
    } catch {
      window.prompt(t('copy'), url);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('shareTitle')} closeLabel={t('close')}>
      <p className="muted" style={{ marginBottom: 12 }}>
        {t('shareHint')}
      </p>
      {error ? (
        <Alert tone="error">{error}</Alert>
      ) : (
        <div className="share-row">
          <input
            className="cfg-input"
            readOnly
            value={url ?? t('loading')}
            aria-label={t('shareTitle')}
            onFocus={(e) => e.target.select()}
          />
          <Button variant="primary" disabled={!url} onClick={() => void copy()}>
            {t('copy')}
          </Button>
          {url && typeof navigator.share === 'function' && (
            <Button onClick={() => void navigator.share({ url }).catch(() => undefined)}>
              {t('share')}
            </Button>
          )}
        </div>
      )}
    </Dialog>
  );
}

export function SummaryDialog({
  open,
  onClose,
  catalog,
  zones,
  selections,
}: {
  open: boolean;
  onClose: () => void;
  catalog: KitchenCatalog;
  zones: ZoneKey[];
  selections: Partial<Record<ZoneKey, KitchenMaterial>>;
}) {
  const { t, locale } = useI18n();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('summaryTitle')}
      closeLabel={t('close')}
      width={720}
    >
      <div className="table-wrap">
        <table className="spec-table">
          <thead>
            <tr>
              <th scope="col">{t('zones')}</th>
              <th scope="col">{t('materials')}</th>
              <th scope="col">{t('code')}</th>
              <th scope="col">{t('sku')}</th>
              <th scope="col">{t('finish')}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {catalog.zones
              .filter((z) => zones.includes(z.key))
              .map((z) => {
                const m = selections[z.key];
                return (
                  <tr key={z.key}>
                    <th scope="row">{pick(z.name, locale)}</th>
                    <td>
                      {m && (
                        <span className="spec-material">
                          <span
                            className="spec-thumb"
                            style={{ backgroundImage: `url("${m.textures.thumbnail}")` }}
                            aria-hidden
                          />
                          {pick(m.name, locale)}
                        </span>
                      )}
                    </td>
                    <td>{m?.code}</td>
                    <td>{m?.erpItemCode}</td>
                    <td>{m ? t(`finish_${m.finish}` as DictKey) : ''}</td>
                    <td>
                      {m && (
                        <Badge tone={stockTone(m.stock.status)}>
                          {t(stockLabelKey(m.stock.status))}
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </Dialog>
  );
}
